import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import adminApi from '../../api/adminClient';
import ContractLayout from '../../components/ContractLayout';
import './Admin.css';

const emptyForm = {
  eventId: '', employeeName: '', phone: '', residentIdMasked: '', address: '', birthDate: '',
  bankName: '', bankAccount: '', emergencyContact: '', memo: '',
  workPlace: '', workPeriodFrom: '', workPeriodTo: '',
  workTask: '', workTimeHours: '', workTimeStart: '', workTimeEnd: '', workTimeBreak: '', breakTimeDesc: '',
  workType: '', workHoursDesc: '', salaryCondition: '',
  salaryCalcFrom: '', salaryCalcTo: '', salaryPaymentDate: '',
  templateId: '',
};

const validatePhone = (p) => /^01[016789]\d{7,8}$/.test(p.replace(/\D/g, ''));
const validateResidentId = (id) => /^\d{6}-\d{7}$/.test(id);

function generatePassword(phone) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let rand = '';
  for (let i = 0; i < 5; i++) rand += chars[Math.floor(Math.random() * chars.length)];
  return rand + phone.replace(/\D/g, '').slice(-4);
}

function fmtDate(d) {
  if (!d) return '';
  const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}년 ${m[2]}월 ${m[3]}일` : String(d);
}

/* ── AdminContracts (목록) ── */
export default function AdminContracts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [contracts, setContracts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [events, setEvents] = useState([]);
  const [companyInfo, setCompanyInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [detailing, setDetailing] = useState(null);
  const [filter, setFilter] = useState('전체');
  const [eventFilter, setEventFilter] = useState('전체');
  const [signedDeleteModal, setSignedDeleteModal] = useState(null); // { contract } | null
  const [signedDeletePw, setSignedDeletePw] = useState('');
  const [signedDeleteError, setSignedDeleteError] = useState('');
  const [signedDeleteLoading, setSignedDeleteLoading] = useState(false);

  useEffect(() => {
    loadAll().then(() => {
      const freelancerId = searchParams.get('freelancerId');
      if (freelancerId) {
        adminApi.get(`/freelancers/${freelancerId}`)
          .then(f => {
            setEditing({
              _fromFreelancer: true, freelancerId: f.id, freelancerEmployeeId: f.employeeId,
              employeeName: f.employeeName, residentIdMasked: f.residentIdMasked || '',
              phone: f.phone, birthDate: f.birthDate,
              address: f.address, bankName: f.bankName, bankAccount: f.bankAccount,
              emergencyContact: f.emergencyContact,
            });
            setSearchParams({}, { replace: true });
          })
          .catch(() => {});
      }
    });
  }, []);

  async function loadAll() {
    try {
      setLoading(true);
      const [contractData, templateData, eventData, coData] = await Promise.all([
        adminApi.get('/contracts'),
        adminApi.get('/contract-templates'),
        adminApi.get('/events'),
        adminApi.get('/company').catch(() => ({})),
      ]);
      setContracts(contractData);
      setTemplates(templateData);
      setEvents(eventData);
      setCompanyInfo(coData);
      setError(null);
      return { contracts: contractData, templates: templateData, events: eventData };
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(c) {
    if (c.isSigned) {
      setSignedDeletePw('');
      setSignedDeleteError('');
      setSignedDeleteModal({ contract: c });
      return;
    }
    if (!window.confirm(`"${c.employeeName}"님의 근로계약서를 완전히 삭제하시겠습니까? 되돌릴 수 없습니다.`)) return;
    try {
      await adminApi.delete(`/contracts/${c.id}`);
      loadAll();
    } catch (err) {
      alert(err.message);
    }
  }

  async function handleSignedDeleteConfirm() {
    if (!signedDeletePw) { setSignedDeleteError('비밀번호를 입력해 주세요.'); return; }
    setSignedDeleteLoading(true);
    setSignedDeleteError('');
    try {
      await adminApi.post('/auth/verify-password', { password: signedDeletePw });
      await adminApi.delete(`/contracts/${signedDeleteModal.contract.id}`);
      setSignedDeleteModal(null);
      loadAll();
    } catch (err) {
      setSignedDeleteError(err.message || '오류가 발생했습니다.');
    } finally {
      setSignedDeleteLoading(false);
    }
  }

  function handleDownload(c) {
    const url = adminApi.fileUrl(`/contracts/${c.id}/download`);
    window.open(url, '_blank');
  }

  const filtered = contracts.filter(c => {
    if (filter === '서명완료' && !c.isSigned) return false;
    if (filter === '서명대기' && c.isSigned) return false;
    if (eventFilter !== '전체' && String(c.eventId) !== String(eventFilter)) return false;
    return true;
  });

  if (detailing !== null) {
    return (
      <AdminLayout>
        <ContractDetail
          contractId={detailing}
          companyInfo={companyInfo}
          templates={templates}
          onBack={() => setDetailing(null)}
          onEdit={(c) => { setEditing(c); setDetailing(null); }}
        />
      </AdminLayout>
    );
  }

  if (editing !== null) {
    return (
      <AdminLayout>
        <ContractForm
          initial={editing}
          templates={templates}
          events={events}
          companyInfo={companyInfo}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadAll(); }}
        />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* 서명 완료 계약서 삭제 확인 모달 */}
      {signedDeleteModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          zIndex: 20000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: '2rem',
            width: '100%', maxWidth: 400, boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#dc2626' }}>서명 완료 계약서 삭제</h3>
            </div>
            <p style={{ marginBottom: '0.4rem', fontSize: '0.95rem' }}>
              <b>{signedDeleteModal.contract.employeeName}</b> 님의 근로계약서는 서명이 완료된 상태입니다.
            </p>
            <p style={{ marginBottom: '1.25rem', fontSize: '0.95rem', color: '#374151' }}>
              삭제하시겠습니까? 관리자 비밀번호를 입력하세요.
            </p>
            <div className="form-group" style={{ marginBottom: '0.75rem' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: 600 }}>관리자 비밀번호</label>
              <input
                type="password"
                value={signedDeletePw}
                onChange={e => { setSignedDeletePw(e.target.value); setSignedDeleteError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleSignedDeleteConfirm()}
                placeholder="비밀번호 입력"
                autoFocus
                style={{ width: '100%', marginTop: '0.3rem' }}
              />
            </div>
            {signedDeleteError && (
              <div className="form-error" style={{ marginBottom: '0.75rem' }}>{signedDeleteError}</div>
            )}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-outline-navy"
                onClick={() => setSignedDeleteModal(null)}
                disabled={signedDeleteLoading}
              >
                취소
              </button>
              <button
                className="btn btn-primary"
                style={{ background: '#dc2626', borderColor: '#dc2626' }}
                onClick={handleSignedDeleteConfirm}
                disabled={signedDeleteLoading}
              >
                {signedDeleteLoading ? '삭제 중...' : '삭제'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="admin-header">
        <div>
          <h1>근로계약서 관리</h1>
          <p className="text-muted">업무를 선택해 등록하면, 해당 인원은 로그인 시 같은 업무 + 비밀번호로 접속합니다.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setEditing({})}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          신규 등록
        </button>
      </div>

      <div className="admin-filter-row">
        {['전체', '서명완료', '서명대기'].map(f => (
          <button
            key={f}
            className={`type-filter__btn ${filter === f ? 'type-filter__btn--active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f} {f !== '전체' && `(${contracts.filter(c => f === '서명완료' ? c.isSigned : !c.isSigned).length})`}
          </button>
        ))}
        <select
          className="admin-status-select"
          value={eventFilter}
          onChange={(e) => setEventFilter(e.target.value)}
          style={{ marginLeft: '0.5rem' }}
        >
          <option value="전체">전체 업무</option>
          {events.map(ev => (
            <option key={ev.id} value={ev.id}>
              {ev.name}({ev.regionSido} {ev.regionSigungu}) - {ev.periodFrom}~{ev.periodTo}
            </option>
          ))}
        </select>
      </div>

      {loading && <div className="state-message">불러오는 중입니다...</div>}
      {!loading && error && <div className="state-message state-message--error">{error}</div>}

      {!loading && !error && (
        filtered.length === 0
          ? <div className="state-message">조건에 맞는 데이터가 없습니다.</div>
          : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th style={{ whiteSpace: 'nowrap' }}>서명상태</th>
                    <th>업무</th>
                    <th style={{ whiteSpace: 'nowrap' }}>이름</th>
                    <th style={{ width: 120 }}>휴대폰</th>
                    <th>근무지</th>
                    <th>근로기간</th>
                    <th>일급</th>
                    <th style={{ width: 170 }}>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(c => (
                    <tr key={c.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <span className={`badge ${c.isSigned ? 'badge-green' : 'badge-accent'}`}>
                          {c.isSigned ? '서명완료' : '서명대기'}
                        </span>
                      </td>
                      <td>{c.eventName || <span className="text-muted">미지정</span>}</td>
                      <td className="admin-table__title" style={{ whiteSpace: 'nowrap' }}>{c.employeeName}</td>
                      <td>{c.phone}</td>
                      <td>{c.workPlace || '-'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{c.workPeriodFrom}<br />~ {c.workPeriodTo}</td>
                      <td>{c.dailyWage ? Number(c.dailyWage).toLocaleString() + '원' : '-'}</td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', alignItems: 'flex-start' }}>
                          <div className="admin-table__actions">
                            <button className="admin-action-btn" onClick={() => setDetailing(c.id)}>상세보기</button>
                            {!c.isSigned && (
                              <button className="admin-action-btn" onClick={() => setEditing(c)}>수정</button>
                            )}
                            {c.isSigned && c.hasSignedFile && (
                              <button className="admin-action-btn" onClick={() => handleDownload(c)}>PDF</button>
                            )}
                          </div>
                          <button className="admin-action-btn admin-action-btn--danger" onClick={() => handleDelete(c)}>삭제</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
      )}
    </AdminLayout>
  );
}

/* ── 상세보기 ── */
function ContractDetail({ contractId, companyInfo, templates, onBack, onEdit }) {
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copyToast, setCopyToast] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenMsg, setRegenMsg] = useState(null);
  const [resigning, setResigning] = useState(false);
  const [resignModal, setResignModal] = useState(false);
  const [resignPw, setResignPw] = useState('');
  const [resignError, setResignError] = useState('');
  const [resignLoading, setResignLoading] = useState(false);

  function loadContract() {
    setLoading(true);
    adminApi.get(`/contracts/${contractId}`)
      .then(setContract)
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadContract(); }, [contractId]);

  if (loading) return <div className="state-message">불러오는 중입니다...</div>;
  if (!contract) return <div className="state-message state-message--error">데이터를 불러올 수 없습니다.</div>;

  const handleRegenerate = async () => {
    if (!window.confirm('기존 PDF를 삭제하고 새로 생성합니다. 계속할까요?')) return;
    setRegenerating(true);
    setRegenMsg(null);
    try {
      await adminApi.post(`/contracts/${contractId}/regenerate-pdf`, {});
      setRegenMsg('PDF 재생성 완료. 다시 다운로드해 주세요.');
    } catch (err) {
      setRegenMsg('재생성 실패: ' + (err.message || '오류'));
    } finally {
      setRegenerating(false);
    }
  };

  const handleResignConfirm = async () => {
    if (!resignPw) { setResignError('비밀번호를 입력해 주세요.'); return; }
    setResignLoading(true);
    setResignError('');
    try {
      await adminApi.post('/auth/verify-password', { password: resignPw });
    } catch (err) {
      setResignError(err.message || '비밀번호가 올바르지 않습니다.');
      setResignLoading(false);
      return;
    }
    setResignModal(false);
    setResignLoading(false);
    setResigning(true);
    setRegenMsg(null);
    try {
      await adminApi.post(`/contracts/${contractId}/reset-signature`, {});
      loadContract();
      setRegenMsg('서명이 초기화되었습니다. 해당 인원이 다시 로그인하면 서명할 수 있습니다.');
    } catch (err) {
      setRegenMsg('초기화 실패: ' + (err.message || '오류'));
    } finally {
      setResigning(false);
    }
  };

  const handleCopyInfo = () => {
    const lines = [
      `업무명: ${contract.eventName || '-'}`,
      `업무기간: ${contract.workPeriodFrom || '-'} ~ ${contract.workPeriodTo || '-'}`,
      `이름: ${contract.employeeName || '-'}`,
      `휴대폰 번호: ${contract.phone}`,
    ];
    if (contract.loginPw) lines.push(`비밀번호: ${contract.loginPw}`);
    lines.push(`근로계약서 서명 : https://hub.me.kr/my/login`);
    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setCopyToast(true);
      setTimeout(() => setCopyToast(false), 2000);
    });
  };

  const handleDownload = () => {
    const url = adminApi.fileUrl(`/contracts/${contractId}/download`);
    window.open(url, '_blank');
  };

  return (
    <div>
      {resignModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          zIndex: 20000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: '2rem',
            width: '100%', maxWidth: 400, boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#dc2626' }}>재서명 요청</h3>
            </div>
            <p style={{ marginBottom: '0.4rem', fontSize: '0.95rem' }}>
              서명이 완료된 근로계약서입니다. 서명을 삭제하시겠습니까?
            </p>
            <p style={{ marginBottom: '1.25rem', fontSize: '0.9rem', color: '#6b7280' }}>
              기존 서명 파일은 삭제되며, 해당 인원이 다시 로그인하면 재서명할 수 있습니다.
            </p>
            <div className="form-group" style={{ marginBottom: '0.75rem' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: 600 }}>관리자 비밀번호</label>
              <input
                type="password"
                value={resignPw}
                onChange={e => { setResignPw(e.target.value); setResignError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleResignConfirm()}
                placeholder="비밀번호 입력"
                autoFocus
                style={{ width: '100%', marginTop: '0.3rem' }}
              />
            </div>
            {resignError && (
              <div className="form-error" style={{ marginBottom: '0.75rem' }}>{resignError}</div>
            )}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-outline-navy"
                onClick={() => setResignModal(false)}
                disabled={resignLoading}
              >
                취소
              </button>
              <button
                className="btn btn-primary"
                style={{ background: '#dc2626', borderColor: '#dc2626' }}
                onClick={handleResignConfirm}
                disabled={resignLoading}
              >
                {resignLoading ? '처리 중...' : '재서명 요청'}
              </button>
            </div>
          </div>
        </div>
      )}

      {copyToast && (
        <div style={{
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          background: 'rgba(0,0,0,0.75)', color: '#fff', padding: '0.65rem 1.5rem',
          borderRadius: '2rem', fontSize: '0.95rem', zIndex: 9999, pointerEvents: 'none',
        }}>
          안내 내용이 복사되었습니다.
        </div>
      )}

      <button className="back-btn" onClick={onBack}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
        목록으로
      </button>

      {/* 액션 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <span className={`badge ${contract.isSigned ? 'badge-green' : 'badge-accent'}`}>
            {contract.isSigned ? '서명완료' : '서명대기'}
          </span>
          {contract.isSigned && contract.signedAt && (
            <span className="text-muted" style={{ fontSize: '0.85rem' }}>서명일: {String(contract.signedAt).slice(0, 10)}</span>
          )}
          {contract.eventName && (
            <span style={{ fontSize: '0.85rem', color: 'var(--navy-700)', fontWeight: 600 }}>{contract.eventName}</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button className="btn btn-outline-navy" onClick={handleCopyInfo}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.35rem' }}><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg>
            안내 복사
          </button>
          {contract.isSigned && (
            <>
              {contract.hasSignedFile && (
                <button className="btn btn-outline-navy" onClick={handleDownload}>PDF 다운로드</button>
              )}
              <button className="btn btn-outline-navy" onClick={handleRegenerate} disabled={regenerating || resigning}>
                {regenerating ? 'PDF 생성 중...' : 'PDF 재생성'}
              </button>
              <button className="btn btn-outline-navy" onClick={() => { setResignPw(''); setResignError(''); setResignModal(true); }} disabled={regenerating || resigning}
                style={{ color: 'var(--red-600,#dc2626)', borderColor: 'var(--red-600,#dc2626)' }}>
                {resigning ? '처리 중...' : '재서명'}
              </button>
            </>
          )}
          {!contract.isSigned && (
            <button className="btn btn-outline-navy" onClick={() => onEdit(contract)}>수정하기</button>
          )}
        </div>
      </div>

      {regenMsg && (
        <div className={`admin-alert ${regenMsg.startsWith('재생성 실패') || regenMsg.startsWith('초기화 실패') ? 'admin-alert--error' : 'admin-alert--success'}`} style={{ marginBottom: '1rem' }}>
          {regenMsg}
        </div>
      )}

      {/* 계약서 레이아웃 (view 모드) */}
      <ContractLayout
        form={contract} mode="view" companyInfo={companyInfo}
        templateName={(templates || []).find(t => String(t.id) === String(contract.templateId))?.name || ''}
      />

      {/* 추가 정보 */}
      {(contract.birthDate || contract.loginPw || contract.memo) && (
        <div className="admin-form-card" style={{ marginTop: '1rem' }}>
          {contract.birthDate && (
            <>
              <h4 className="admin-form-section-title">추가 인적 정보</h4>
              <div className="admin-detail-grid">
                <div className="admin-detail-item">
                  <span className="admin-detail-item__label">생년월일</span>
                  <span className="admin-detail-item__value">{contract.birthDate}</span>
                </div>
              </div>
            </>
          )}
          {contract.loginPw && (
            <>
              <h4 className="admin-form-section-title">접속 비밀번호</h4>
              <div className="admin-detail-grid">
                <div className="admin-detail-item">
                  <span className="admin-detail-item__label">비밀번호</span>
                  <span className="admin-detail-item__value" style={{ fontWeight: 700, letterSpacing: '0.08em', color: 'var(--navy-700)', fontSize: '1.05rem' }}>
                    {contract.loginPw}
                  </span>
                </div>
              </div>
            </>
          )}
          {contract.memo && (
            <>
              <h4 className="admin-form-section-title">관리자 메모</h4>
              <p style={{ color: 'var(--gray-700)', whiteSpace: 'pre-wrap', fontSize: '0.95rem' }}>{contract.memo}</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ── 등록/수정 폼 ── */
function ContractForm({ initial, templates, events, companyInfo, onCancel, onSaved }) {
  const isEdit = !!initial.id;
  const [freelancers, setFreelancers] = useState([]);
  const [selectedFreelancerId, setSelectedFreelancerId] = useState(initial._fromFreelancer ? String(initial.freelancerId) : '');
  const [form, setForm] = useState(() => {
    if (!isEdit) {
      return {
        ...emptyForm,
        templateId: templates.find(t => t.is_active)?.id || templates[0]?.id || '',
        employeeName: initial.employeeName || '',
        phone: initial.phone || '',
        birthDate: initial.birthDate || '',
        address: initial.address || '',
        bankName: initial.bankName || '',
        bankAccount: initial.bankAccount || '',
        emergencyContact: initial.emergencyContact || '',
      };
    }
    return {
      eventId: initial.eventId || '',
      employeeName: initial.employeeName || '',
      phone: initial.phone || '',
      residentIdMasked: initial.residentIdMasked || '',
      address: initial.address || '',
      birthDate: initial.birthDate || '',
      bankName: initial.bankName || '',
      bankAccount: initial.bankAccount || '',
      emergencyContact: initial.emergencyContact || '',
      memo: initial.memo || '',
      workPlace: initial.workPlace || '',
      workPeriodFrom: initial.workPeriodFrom || '',
      workPeriodTo: initial.workPeriodTo || '',
      workTask: initial.workTask || '',
      workTimeHours: initial.workTimeHours || '',
      workTimeStart: initial.workTimeStart || '',
      workTimeEnd: initial.workTimeEnd || '',
      workTimeBreak: initial.workTimeBreak || '',
      breakTimeDesc: initial.breakTimeDesc || '',
      workType: initial.workType || '',
      workHoursDesc: initial.workHoursDesc || '',
      salaryCondition: initial.salaryCondition || '',
      salaryCalcFrom: initial.salaryCalcFrom || '',
      salaryCalcTo: initial.salaryCalcTo || '',
      salaryPaymentDate: initial.salaryPaymentDate || '',
      templateId: initial.templateId || '',
      otherQ3: initial.otherQ3 ?? null,
    };
  });
  const [phoneError, setPhoneError] = useState('');
  const [residentIdError, setResidentIdError] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [createdResult, setCreatedResult] = useState(null);
  const [copyToast, setCopyToast] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [loadModal, setLoadModal] = useState(false);
  const [loadEventId, setLoadEventId] = useState('');
  const [loadLoading, setLoadLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!isEdit) adminApi.get('/freelancers').then(setFreelancers).catch(() => {});
  }, [isEdit]);

  const handleFreelancerSelect = (e) => {
    const fId = e.target.value;
    setSelectedFreelancerId(fId);
    if (!fId) return;
    const f = freelancers.find(fl => String(fl.id) === fId);
    if (f) {
      setForm(prev => ({
        ...prev,
        employeeName: f.employeeName,
        residentIdMasked: f.residentIdMasked || prev.residentIdMasked,
        phone: f.phone,
        birthDate: f.birthDate || prev.birthDate,
        address: f.address || prev.address,
        bankName: f.bankName || prev.bankName,
        bankAccount: f.bankAccount || prev.bankAccount,
        emergencyContact: f.emergencyContact || prev.emergencyContact,
      }));
      setPhoneError('');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updates = { [name]: value };
    if (name === 'workPeriodFrom') updates.salaryCalcFrom = value;
    if (name === 'workPeriodTo') updates.salaryCalcTo = value;
    setForm(prev => ({ ...prev, ...updates }));
  };

  const handleEventChange = (e) => {
    const evId = e.target.value;
    const selectedEvent = events.find(ev => String(ev.id) === evId);
    setForm(prev => ({
      ...prev,
      eventId: evId,
      workPeriodFrom: selectedEvent?.periodFrom || prev.workPeriodFrom,
      workPeriodTo: selectedEvent?.periodTo || prev.workPeriodTo,
      salaryCalcFrom: selectedEvent?.periodFrom || prev.salaryCalcFrom,
      salaryCalcTo: selectedEvent?.periodTo || prev.salaryCalcTo,
    }));
  };

  async function handleLoadContent() {
    if (!loadEventId) { setLoadError('업무를 선택해 주세요.'); return; }
    setLoadLoading(true);
    setLoadError('');
    try {
      const data = await adminApi.get(`/contract-contents/${loadEventId}`);
      setForm(prev => ({
        ...prev,
        templateId:        data.template_id         ? String(data.template_id) : prev.templateId,
        workPeriodFrom:    data.work_period_from     || prev.workPeriodFrom,
        workPeriodTo:      data.work_period_to       || prev.workPeriodTo,
        workTask:          data.work_task            || prev.workTask,
        workPlace:         data.work_place           || prev.workPlace,
        workTimeHours:     data.work_time_hours      || prev.workTimeHours,
        workTimeStart:     data.work_time_start      || prev.workTimeStart,
        workTimeEnd:       data.work_time_end        || prev.workTimeEnd,
        workTimeBreak:     data.work_time_break      || prev.workTimeBreak,
        breakTimeDesc:     data.break_time_desc      || prev.breakTimeDesc,
        workType:          data.work_type            || prev.workType,
        workHoursDesc:     data.work_hours_desc      || prev.workHoursDesc,
        salaryCondition:   data.salary_condition     || prev.salaryCondition,
        salaryCalcFrom:    data.salary_calc_from     || prev.salaryCalcFrom,
        salaryCalcTo:      data.salary_calc_to       || prev.salaryCalcTo,
        salaryPaymentDate: data.salary_payment_date  || prev.salaryPaymentDate,
      }));
      setLoadModal(false);
    } catch (err) {
      setLoadError(err.message || '불러오기에 실패했습니다. 해당 업무의 저장된 내용이 없을 수 있습니다.');
    } finally {
      setLoadLoading(false);
    }
  }

  const handlePhoneChange = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '');
    setForm(prev => ({ ...prev, phone: val }));
    setPhoneError(val && !validatePhone(val) ? '01X로 시작하는 10~11자리 숫자를 입력해 주세요.' : '');
  };

  const handleResidentIdChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 13);
    const val = digits.length > 6 ? digits.slice(0, 6) + '-' + digits.slice(6) : digits;
    setForm(prev => ({ ...prev, residentIdMasked: val }));
    setResidentIdError(val && !validateResidentId(val) ? '형식: 990101-1234567' : '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const REQUIRED_FIELDS = [
      { key: 'eventId',          label: '업무' },
      { key: 'employeeName',     label: '이름' },
      { key: 'residentIdMasked', label: '주민번호' },
      { key: 'phone',            label: '연락처' },
      { key: 'address',          label: '주소' },
      { key: 'bankName',         label: '은행' },
      { key: 'bankAccount',      label: '계좌번호' },
      { key: 'workPeriodFrom',   label: '근로 시작일' },
      { key: 'workPeriodTo',     label: '근로 종료일' },
      { key: 'workTask',         label: '담당업무' },
      { key: 'workPlace',        label: '업무장소' },
      { key: 'workTimeHours',    label: '근로시간' },
      { key: 'workTimeStart',    label: '시업시간' },
      { key: 'workTimeEnd',      label: '종업시간' },
      { key: 'workTimeBreak',    label: '휴게시간' },
      { key: 'breakTimeDesc',    label: '휴게시간 상세' },
      { key: 'workType',         label: '근로형태' },
      { key: 'workHoursDesc',    label: '근로시간 설명' },
      { key: 'salaryCondition',  label: '급여조건' },
      { key: 'salaryCalcFrom',   label: '임금산정기간 시작' },
      { key: 'salaryCalcTo',     label: '임금산정기간 종료' },
      { key: 'salaryPaymentDate', label: '임금지급일' },
    ];
    const missing = REQUIRED_FIELDS.filter(({ key }) => !form[key]).map(({ label }) => label);
    if (missing.length > 0) {
      setError(`다음 항목은 필수입니다: ${missing.join(', ')}`);
      return;
    }
    if (!validatePhone(form.phone)) {
      setError('휴대폰번호 형식이 올바르지 않습니다. (예: 01012345678)');
      return;
    }
    if (!validateResidentId(form.residentIdMasked)) {
      setError('주민번호 형식이 올바르지 않습니다. (예: 990101-1234567)');
      return;
    }

    if (!isEdit) {
      try {
        const dup = await adminApi.get(
          `/contracts/check-duplicate?eventId=${form.eventId}&employeeName=${encodeURIComponent(form.employeeName)}&phone=${form.phone}`
        );
        if (dup.isDuplicate) { setError('동일한 인력의 근로계약서가 등록되어 있습니다.'); return; }
      } catch (_) {}
    }

    setSaving(true);
    setError(null);

    const selectedFreelancer = freelancers.find(f => String(f.id) === selectedFreelancerId);
    const payload = {
      ...form,
      freelancerEmployeeId: selectedFreelancer?.employeeId || initial.freelancerEmployeeId || null,
    };
    if (!isEdit) payload.loginPw = generatePassword(form.phone);

    try {
      if (isEdit) {
        await adminApi.put(`/contracts/${initial.id}`, payload);
        onSaved();
      } else {
        const result = await adminApi.post('/contracts', payload);
        // 업무별 계약서 내용 최초 1회만 저장 (이미 존재하면 저장하지 않음)
        if (form.eventId) {
          adminApi.get(`/contract-contents/${form.eventId}`)
            .catch(() => {
              adminApi.put(`/contract-contents/${form.eventId}`, {
                templateId:        form.templateId || null,
                workPeriodFrom:    form.workPeriodFrom,
                workPeriodTo:      form.workPeriodTo,
                workTask:          form.workTask,
                workPlace:         form.workPlace,
                workTimeHours:     form.workTimeHours,
                workTimeStart:     form.workTimeStart,
                workTimeEnd:       form.workTimeEnd,
                workTimeBreak:     form.workTimeBreak,
                breakTimeDesc:     form.breakTimeDesc,
                workType:          form.workType,
                workHoursDesc:     form.workHoursDesc,
                salaryCondition:   form.salaryCondition,
                salaryCalcFrom:    form.salaryCalcFrom,
                salaryCalcTo:      form.salaryCalcTo,
                salaryPaymentDate: form.salaryPaymentDate,
              }).catch(() => {});
            });
        }
        setCreatedResult({ ...result, loginPw: payload.loginPw });
      }
    } catch (err) {
      setError(err.message || '저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  /* 등록 완료 화면 */
  if (createdResult) {
    const selectedEvent = events.find(ev => String(ev.id) === String(form.eventId));
    const handleCopyInfo = () => {
      const text = [
        `업무명: ${selectedEvent?.name || '-'}`,
        `업무기간: ${selectedEvent?.periodFrom || '-'} ~ ${selectedEvent?.periodTo || '-'}`,
        `이름: ${form.employeeName || '-'}`,
        `휴대폰 번호: ${createdResult.loginPhone}`,
        `비밀번호: ${createdResult.loginPw}`,
        `근로계약서 서명 : https://hub.me.kr/my/login`,
      ].join('\n');
      navigator.clipboard.writeText(text).then(() => {
        setCopyToast(true);
        setTimeout(() => setCopyToast(false), 2000);
      });
    };
    return (
      <div className="admin-form-card" style={{ maxWidth: 520, position: 'relative' }}>
        {copyToast && (
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
            background: 'rgba(0,0,0,0.75)', color: '#fff', padding: '0.65rem 1.5rem',
            borderRadius: '2rem', fontSize: '0.95rem', zIndex: 9999, pointerEvents: 'none',
          }}>
            안내 내용이 복사되었습니다.
          </div>
        )}
        <h2>등록이 완료되었습니다</h2>
        <div className="form-error" style={{ background: '#d1fae5', color: '#065f46', marginBottom: '1.5rem' }}>
          아래 정보를 본인에게 안내해 주세요.
        </div>
        <div className="admin-detail-grid" style={{ marginBottom: '1.75rem' }}>
          <div className="admin-detail-item">
            <span className="admin-detail-item__label">업무명</span>
            <span className="admin-detail-item__value">{selectedEvent?.name || '-'}</span>
          </div>
          <div className="admin-detail-item">
            <span className="admin-detail-item__label">업무기간</span>
            <span className="admin-detail-item__value">{selectedEvent?.periodFrom || '-'} ~ {selectedEvent?.periodTo || '-'}</span>
          </div>
          <div className="admin-detail-item">
            <span className="admin-detail-item__label">휴대폰 번호</span>
            <span className="admin-detail-item__value">{createdResult.loginPhone}</span>
          </div>
          <div className="admin-detail-item">
            <span className="admin-detail-item__label">비밀번호</span>
            <span className="admin-detail-item__value" style={{ fontWeight: 700, letterSpacing: '0.08em', color: 'var(--navy-700)', fontSize: '1.1rem' }}>
              {createdResult.loginPw}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn btn-outline-navy" style={{ flex: 1 }} onClick={handleCopyInfo}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.35rem' }}><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg>
            안내 복사
          </button>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={onSaved}>목록으로</button>
        </div>
      </div>
    );
  }

  const activeEvents = events.filter(
    ev => ev.periodTo >= new Date().toISOString().slice(0, 10) && ev.status !== '미사용'
  );

  return (
    <div>
      <button className="back-btn" onClick={onCancel}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
        목록으로
      </button>

      {/* 불러오기 팝업 */}
      {loadModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
          onClick={e => { if (e.target === e.currentTarget) setLoadModal(false); }}
        >
          <div style={{
            background: '#fff', borderRadius: 8, padding: '2rem',
            width: '100%', maxWidth: 440, boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
          }}>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 700, color: 'var(--navy-800)' }}>계약서 내용 불러오기</h3>
            <p style={{ margin: '0 0 1.25rem', fontSize: '0.875rem', color: '#6b7280' }}>
              업무를 선택하면 해당 업무에 저장된 계약서 조항 내용을 불러옵니다.<br />
              갑·을 정보 및 추가 인적 정보는 변경되지 않습니다.
            </p>
            <div className="form-group" style={{ marginBottom: '0.75rem' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: 600 }}>업무 선택</label>
              <select
                value={loadEventId}
                onChange={e => { setLoadEventId(e.target.value); setLoadError(''); }}
                style={{ marginTop: '0.3rem' }}
              >
                <option value="">업무를 선택하세요</option>
                {events.map(ev => (
                  <option key={ev.id} value={ev.id}>
                    {ev.name} ({ev.regionSido} {ev.regionSigungu}) - {ev.periodFrom}~{ev.periodTo}
                  </option>
                ))}
              </select>
            </div>
            {loadError && (
              <div className="form-error" style={{ marginBottom: '0.75rem' }}>{loadError}</div>
            )}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-outline-navy" onClick={() => setLoadModal(false)} disabled={loadLoading}>취소</button>
              <button className="btn btn-primary" onClick={handleLoadContent} disabled={loadLoading}>
                {loadLoading ? '불러오는 중...' : '불러오기'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 헤더 선택 영역 */}
      {showPreview && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          zIndex: 10000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          overflowY: 'auto', padding: '2rem 1rem',
        }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowPreview(false); }}
        >
          <div style={{ background: '#f5f5f5', borderRadius: 8, padding: '1.5rem', maxWidth: 760, width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>근로계약서 미리보기</h3>
              <button className="btn btn-outline-navy" onClick={() => setShowPreview(false)}>닫기</button>
            </div>
            <ContractLayout
              form={form} mode="view" companyInfo={companyInfo}
              templateName={templates.find(t => String(t.id) === String(form.templateId))?.name || ''}
            />
          </div>
        </div>
      )}

      <div className="admin-form-card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>{isEdit ? '근로계약서 수정' : '근로계약서 등록'}</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {!isEdit && (
              <button type="button" className="btn btn-outline-navy" onClick={() => { setLoadEventId(''); setLoadError(''); setLoadModal(true); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.3rem' }}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                불러오기
              </button>
            )}
            <button type="button" className="btn btn-outline-navy" onClick={() => setShowPreview(true)}>미리보기</button>
          </div>
        </div>

        {!isEdit && (
          <div className="form-error" style={{ background: 'var(--blue-50)', color: 'var(--navy-700)', marginBottom: '1rem' }}>
            등록 완료 후 자동 생성된 비밀번호를 본인에게 안내해 주세요. 비밀번호는 5자리 영숫자 + 휴대폰번호 뒷 4자리 조합으로 생성됩니다.
          </div>
        )}

        {error && <div className="form-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}

        <div className="form-row-2">
          {/* 템플릿 선택 */}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>계약서 템플릿</label>
            <select name="templateId" value={form.templateId} onChange={handleChange}>
              <option value="">템플릿 선택 (없어도 무관)</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name}{t.is_active ? ' (기본)' : ''}</option>
              ))}
            </select>
          </div>
          {/* 업무 선택 */}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>업무 <span className="required">*</span></label>
            <select name="eventId" value={form.eventId} onChange={handleEventChange}>
              <option value="">업무를 선택하세요</option>
              {activeEvents.map(ev => (
                <option key={ev.id} value={ev.id}>
                  {ev.name}({ev.regionSido} {ev.regionSigungu}) - {ev.periodFrom}~{ev.periodTo}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* 프리랜서 선택 (신규 등록 시) */}
        {!isEdit && (
          <div className="form-group" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
            <label>프리랜서 선택</label>
            <select value={selectedFreelancerId} onChange={handleFreelancerSelect}>
              <option value="">직접 입력 (새 인력 등록)</option>
              {freelancers.map(f => (
                <option key={f.id} value={f.id}>
                  {f.employeeName} ({f.phone}) — {f.employeeId}
                </option>
              ))}
            </select>
            <p className="my-login__hint" style={{ marginTop: '0.3rem' }}>
              프리랜서를 선택하면 인적 정보가 자동으로 채워집니다.
            </p>
          </div>
        )}
      </div>

      {/* 계약서 레이아웃 (edit 모드) */}
      <form onSubmit={handleSubmit}>
        <ContractLayout
          form={form}
          mode="edit"
          onChange={handleChange}
          onPhoneChange={handlePhoneChange}
          onResidentIdChange={handleResidentIdChange}
          companyInfo={companyInfo}
          phoneError={phoneError}
          residentIdError={residentIdError}
          templateName={templates.find(t => String(t.id) === String(form.templateId))?.name || ''}
        />

        {/* 추가 인적 정보 */}
        <div className="admin-form-card" style={{ marginTop: '1rem' }}>
          <h4 className="admin-form-section-title" style={{ marginTop: 0 }}>추가 인적 정보</h4>
          <div className="form-group">
            <label>생년월일</label>
            <input type="date" name="birthDate" value={form.birthDate} onChange={handleChange} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>관리자 메모</label>
            <textarea name="memo" value={form.memo} onChange={handleChange} rows={2} placeholder="특이사항이 있으면 입력하세요." />
          </div>
        </div>

        <div className="admin-form-actions" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--gray-100)' }}>
          <button type="button" className="btn btn-outline-navy" onClick={onCancel}>취소</button>
          <button type="button" className="btn btn-outline-navy" onClick={() => setShowPreview(true)}>미리보기</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? '저장 중...' : isEdit ? '수정 완료' : '등록하기'}
          </button>
        </div>
      </form>
    </div>
  );
}
